import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";

import { useCacheMutations } from "@/shared/cache";
import type { Attachment } from "@/shared/types/entries";
import { useApi } from "@/shared/utils/request";
import { actingHeaders } from "@/v2/hooks/actions/types";
import { useAttachmentUpload } from "@/v2/hooks/useAttachmentUpload";

export type AttachmentChanges = {
	files: File[];
	removedIds: string[];
};

function addAttachment(list: Attachment[], attachment: Attachment) {
	if (list.some((a) => a.id === attachment.id)) return list;
	return [...list, attachment];
}

export function useEntryAttachmentMutations(entryId: string | undefined) {
	const { uploadFile } = useAttachmentUpload();
	const { makeApiRequest } = useApi();
	const { optimisticUpdate, refresh } = useCacheMutations();

	const upload = useMutation({
		mutationFn: async ({
			file,
			timekeeperId,
			onProgress,
		}: {
			file: File;
			timekeeperId?: string | null;
			onProgress?: (loaded: number, total: number) => void;
		}) => {
			if (!entryId) throw new Error("No entry id");
			return uploadFile(entryId, file, {
				timekeeperId,
				onProgress,
			});
		},
		onSuccess: (attachment) => {
			if (!entryId) return;
			optimisticUpdate("entries", (entries) =>
				entries.map((entry) =>
					entry.id === entryId
						? {
								...entry,
								attachments: addAttachment(entry.attachments ?? [], attachment),
							}
						: entry,
				),
			);
			void refresh("entries");
		},
	});

	const remove = useMutation({
		mutationFn: async ({
			attachmentId,
			timekeeperId,
		}: {
			attachmentId: string;
			timekeeperId?: string | null;
		}) => {
			if (!entryId) throw new Error("No entry id");
			const resp = await makeApiRequest({
				endpoint: `/v2/entries/${entryId}/attachments/${attachmentId}`,
				method: "DELETE",
				headers: actingHeaders(timekeeperId),
			});
			if (resp.status === "ERROR") {
				throw new Error(resp.error || "Failed to remove attachment");
			}
			return attachmentId;
		},
		onMutate: async ({ attachmentId }) => {
			if (!entryId) return undefined;
			return optimisticUpdate("entries", (entries) =>
				entries.map((entry) =>
					entry.id === entryId
						? {
								...entry,
								attachments: (entry.attachments ?? []).filter(
									(a) => a.id !== attachmentId,
								),
							}
						: entry,
				),
			);
		},
		onError: (err, _vars, rollback) => {
			rollback?.();
			toast.error(
				err instanceof Error ? err.message : "Failed to remove attachment",
			);
		},
		onSuccess: () => {
			void refresh("entries");
		},
	});

	return { upload, remove };
}

/** Upload selected create-mode files after POST /v2/entries succeeds. */
export function useAttachmentCommit() {
	const { uploadFile } = useAttachmentUpload();
	const { refresh } = useCacheMutations();

	return useCallback(
		async (
			entryId: string,
			changes: AttachmentChanges,
			timekeeperId?: string | null,
		) => {
			const { files } = changes;
			if (files.length === 0) return;

			let uploaded = 0;
			const queue = [...files];
			const worker = async () => {
				while (queue.length) {
					const file = queue.shift();
					if (!file) break;
					try {
						await uploadFile(entryId, file, { timekeeperId });
						uploaded += 1;
					} catch {
						toast.error(`Failed to upload ${file.name}`);
					}
				}
			};
			await Promise.all(
				Array.from({ length: Math.min(3, queue.length) }, worker),
			);

			if (uploaded > 0) {
				toast.success(`Uploaded ${uploaded} file${uploaded === 1 ? "" : "s"}`);
				await refresh("entries");
			}
		},
		[refresh, uploadFile],
	);
}
